<template>
	<SuiDataTableRow class="project-profit-table-revenue__regular-row">
		<SuiDataTableCell
			v-for="item in revenueList"
			:key="item.id"
			:align="item.align"
		>
			{{ item.data }}
		</SuiDataTableCell>
	</SuiDataTableRow>
</template>

<script setup lang="ts">
import {
	SuiDataTableCell,
	SuiDataTableColumnAlign,
	SuiDataTableRow,
} from '@developers/scoro-ui';
import { computed, toRefs } from 'vue';
import { useFormatter } from '@/composables';
import { ProjectProfitTableColumnTypes } from './types';

const props = defineProps<{
	revenueBudgeted: number;
	revenueEarned: number;
	revenueRecognized: number;
	revenueInvoiced: number;
	revenueForecasted: number;
	isBudgetSet: boolean;
	isAnyBudgetSet: boolean;
	canSeeRecognized: boolean;
	canSeeForecast: boolean;
	isRevenueTmModel: boolean;
}>();

const {
	revenueBudgeted,
	revenueEarned,
	revenueRecognized,
	revenueInvoiced,
	revenueForecasted,
	isBudgetSet,
	isAnyBudgetSet,
	canSeeRecognized,
	canSeeForecast,
	isRevenueTmModel,
} = toRefs(props);

const { formatNumber } = useFormatter();
const revenueForecastedTotal = computed(
	() => revenueForecasted.value + revenueRecognized.value
);

const isRevenueOverRecognized = computed(
	() => revenueBudgeted.value < revenueRecognized.value
);
const isRevenueOverInvoicedComparedToBudget = computed(
	() => revenueBudgeted.value < revenueInvoiced.value
);
const isRevenueOverInvoicedComparedToRecognized = computed(
	() => revenueRecognized.value < revenueInvoiced.value
);
const revenueToRecognize = computed(
	() => revenueBudgeted.value - revenueRecognized.value
);
const revenueToInvoice = computed(
	() => revenueBudgeted.value - revenueInvoiced.value
);

const revenueList = computed(() => {
	return [
		{
			id: ProjectProfitTableColumnTypes.BUDGETED,
			data: formatNumber(revenueBudgeted.value),
			align: SuiDataTableColumnAlign.RIGHT,
		},
		...(canSeeRecognized.value
			? [
					{
						id: ProjectProfitTableColumnTypes.EARNED,
						data: formatNumber(revenueEarned.value),
						align: SuiDataTableColumnAlign.RIGHT,
					},
				]
			: []),
		...(canSeeRecognized.value
			? [
					{
						id: ProjectProfitTableColumnTypes.RECOGNIZED_TO_DATE,
						data: formatNumber(revenueRecognized.value),
						align: SuiDataTableColumnAlign.RIGHT,
					},
				]
			: []),
		...(canSeeRecognized.value &&
		!isRevenueTmModel.value &&
		isAnyBudgetSet.value
			? [
					{
						id: ProjectProfitTableColumnTypes.TO_RECOGNIZE,
						data: isRevenueOverRecognized.value
							? undefined
							: formatNumber(revenueToRecognize.value),
					},
				]
			: []),
		{
			id: ProjectProfitTableColumnTypes.INVOICED_TO_DATE,
			data: formatNumber(revenueInvoiced.value),
			align: SuiDataTableColumnAlign.RIGHT,
		},
		...(isBudgetSet.value && !isRevenueTmModel.value
			? [
					{
						id: ProjectProfitTableColumnTypes.TO_INVOICE,
						data: isRevenueOverInvoicedComparedToBudget.value
							? undefined
							: formatNumber(revenueToInvoice.value),
					},
				]
			: [
					{
						id: ProjectProfitTableColumnTypes.TO_INVOICE,
						data: isRevenueOverInvoicedComparedToRecognized.value
							? undefined
							: formatNumber(
									revenueRecognized.value -
										revenueInvoiced.value
								),
					},
				]),
		...(canSeeForecast.value
			? [
					{
						id: ProjectProfitTableColumnTypes.FORECASTED,
						data: formatNumber(revenueForecastedTotal.value),
						align: SuiDataTableColumnAlign.RIGHT,
					},
				]
			: []),
	];
});
</script>

<style scoped lang="scss">
.project-profit-table-revenue {
	&__regular-row {
		--sui-data-table-cell-padding: 2rem;

		line-height: 2.5rem;
		text-wrap: nowrap;

		.sui-data-table-col:last-child {
			padding-right: 1.5rem !important;
		}
	}
}
</style>
